import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, Save, Loader2, Mail, Send, Key, 
  CheckCircle, XCircle, Eye, AlertTriangle, RefreshCw
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

import { BACKEND_URL } from '../config/api';

export default function AdminEmailSettings() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [debugLoading, setDebugLoading] = useState(false);
  
  const [settings, setSettings] = useState({
    email_enabled: true,
    sender_name: 'OnsiteHand',
    sender_email: 'hello@onsitehand.co.uk',
    reply_to: 'support@onsitehand.co.uk',
    api_key_configured: false,
    api_key_prefix: null
  });
  
  const [testEmail, setTestEmail] = useState('');
  const [debugData, setDebugData] = useState(null);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);

  // Idempotency ID for the current in-flight Send-click.
  // One deliberate click = one UUID; transport retries of that click reuse it;
  // a new click generates a new UUID. Server rejects the request without it.
  const testSendRequestIdRef = useRef(null);

  useEffect(() => {
    fetchSettings();
    checkSuperAdmin();
  }, []);

  const checkSuperAdmin = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/auth/me`, {
        withCredentials: true
      });
      setIsSuperAdmin(response.data.admin_role === 'super_admin' || response.data.user_type === 'admin');
    } catch (error) {
      console.error('Failed to check admin status:', error);
    }
  };

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/email-settings`, {
        withCredentials: true
      });
      setSettings(response.data);
    } catch (error) {
      console.error('Failed to fetch email settings:', error);
      toast.error('Failed to load email settings');
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      await axios.put(`${BACKEND_URL}/api/admin/email-settings`, {
        email_enabled: settings.email_enabled,
        sender: {
          sender_name: settings.sender_name,
          sender_email: settings.sender_email,
          reply_to_email: settings.reply_to
        }
      }, {
        withCredentials: true
      });
      toast.success('Email settings saved');
      fetchSettings();
    } catch (error) {
      console.error('Failed to save settings:', error);
      toast.error(error.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const sendTestEmail = async () => {
    if (!testEmail) {
      toast.error('Please enter a recipient email');
      return;
    }

    // Generate a fresh idempotency ID for THIS deliberate click if no click
    // is already in flight. If one is in flight (e.g. React StrictMode double
    // invoke, or the button is smashed twice before the disabled state kicks
    // in), reuse the same id so the backend collapses it.
    if (!testSendRequestIdRef.current) {
      // RFC 4122 v4 via crypto.randomUUID when available; safe fallback otherwise.
      const uuid =
        (typeof crypto !== 'undefined' && crypto.randomUUID)
          ? crypto.randomUUID()
          : `click_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 14)}`;
      testSendRequestIdRef.current = `click_${uuid.replace(/-/g, '').slice(0, 24)}`;
    }
    const request_id = testSendRequestIdRef.current;

    setTesting(true);
    try {
      const response = await axios.post(`${BACKEND_URL}/api/admin/email-settings/test-send`, {
        recipient_email: testEmail,
        request_id,
      }, {
        withCredentials: true
      });

      toast.success(`Test email sent! ID: ${response.data.email_id}`);
    } catch (error) {
      console.error('Failed to send test email:', error);
      toast.error(error.response?.data?.detail || 'Failed to send test email');
    } finally {
      // Clear so the NEXT deliberate click generates a fresh id.
      testSendRequestIdRef.current = null;
      setTesting(false);
    }
  };

  const resetToDefaults = async () => {
    if (!window.confirm(
      'This will reset email settings to safe production defaults:\n\n' +
      '• Sender: OnsiteHand <hello@onsitehand.co.uk>\n' +
      '• Reply-To: support@onsitehand.co.uk\n' +
      '• API Key will be read from environment variable ONLY\n\n' +
      'Any API key stored in the database will be removed.\n\n' +
      'Continue?'
    )) {
      return;
    }
    
    setResetting(true);
    try {
      const response = await axios.post(`${BACKEND_URL}/api/admin/email-settings/reset-to-defaults`, {}, {
        withCredentials: true
      });
      
      toast.success('Email settings reset to safe defaults');
      fetchSettings();
      loadDebugData();
    } catch (error) {
      console.error('Failed to reset settings:', error);
      toast.error(error.response?.data?.detail || 'Failed to reset settings');
    } finally {
      setResetting(false);
    }
  };

  const loadDebugData = async () => {
    setDebugLoading(true);
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/email-settings/debug`, {
        withCredentials: true
      });
      setDebugData(response.data);
    } catch (error) {
      console.error('Failed to load debug data:', error);
      toast.error('Failed to load debug data');
    } finally {
      setDebugLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center gap-4">
          <button
            onClick={() => navigate('/admin/dashboard')}
            className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-xl font-semibold text-slate-900">Email Settings</h1>
            <p className="text-sm text-slate-500">Configure email sender and test delivery</p>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8">
        {/* Status Card */}
        <div className="bg-white rounded-xl border p-6 mb-6">
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <Mail className="w-5 h-5 text-orange-500" />
            Email Status
          </h2>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="p-3 bg-slate-50 rounded-lg">
              <div className="text-xs text-slate-500 mb-1">Email Sending</div>
              <div className={`font-medium ${settings.email_enabled ? 'text-green-600' : 'text-red-600'}`}>
                {settings.email_enabled ? 'Enabled' : 'Disabled'}
              </div>
            </div>
            <div className="p-3 bg-slate-50 rounded-lg">
              <div className="text-xs text-slate-500 mb-1">API Key</div>
              <div className={`font-medium ${settings.api_key_configured ? 'text-green-600' : 'text-red-600'}`}>
                {settings.api_key_configured ? 'Configured' : 'Not Set'}
              </div>
            </div>
            <div className="p-3 bg-slate-50 rounded-lg">
              <div className="text-xs text-slate-500 mb-1">API Key Source</div>
              <div className="font-medium text-slate-700">Environment Variable</div>
            </div>
            <div className="p-3 bg-slate-50 rounded-lg">
              <div className="text-xs text-slate-500 mb-1">Verified Sender</div>
              <div className="font-medium text-slate-700 text-sm truncate">{settings.verified_sender || 'hello@onsitehand.co.uk'}</div>
            </div>
          </div>
          
          {settings.api_key_prefix && (
            <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg">
              <div className="flex items-center gap-2 text-green-800">
                <CheckCircle className="w-4 h-4" />
                <span className="text-sm">API Key: {settings.api_key_prefix}</span>
              </div>
            </div>
          )}
        </div>

        {/* Sender Settings */}
        <div className="bg-white rounded-xl border p-6 mb-6">
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <Send className="w-5 h-5 text-orange-500" />
            Sender Settings
          </h2>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Sender Name
              </label>
              <input
                type="text"
                value={settings.sender_name}
                onChange={(e) => setSettings({ ...settings, sender_name: e.target.value })}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                placeholder="OnsiteHand"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Sender Email
              </label>
              <input
                type="email"
                value={settings.sender_email}
                onChange={(e) => setSettings({ ...settings, sender_email: e.target.value })}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                placeholder="hello@onsitehand.co.uk"
              />
              <p className="text-xs text-slate-500 mt-1">Must be a verified domain in Resend</p>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Reply-To Email
              </label>
              <input
                type="email"
                value={settings.reply_to}
                onChange={(e) => setSettings({ ...settings, reply_to: e.target.value })}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                placeholder="support@onsitehand.co.uk"
              />
            </div>
            
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="email_enabled"
                checked={settings.email_enabled}
                onChange={(e) => setSettings({ ...settings, email_enabled: e.target.checked })}
                className="w-4 h-4 text-orange-500 border-slate-300 rounded focus:ring-orange-500"
              />
              <label htmlFor="email_enabled" className="text-sm text-slate-700">
                Enable email sending
              </label>
            </div>
            
            <button
              onClick={saveSettings}
              disabled={saving}
              className="inline-flex items-center gap-2 px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-lg transition-colors disabled:opacity-50"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              Save Settings
            </button>
          </div>
        </div>

        {/* Test Email */}
        <div className="bg-white rounded-xl border p-6 mb-6">
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <Mail className="w-5 h-5 text-orange-500" />
            Send Test Email
          </h2>
          
          <div className="flex gap-2">
            <input
              type="email"
              value={testEmail}
              onChange={(e) => setTestEmail(e.target.value)}
              placeholder="Enter recipient email"
              className="flex-1 px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
            />
            <button
              onClick={sendTestEmail}
              disabled={testing || !settings.api_key_configured}
              className="inline-flex items-center gap-2 px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-lg transition-colors disabled:opacity-50"
            >
              {testing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              Send Test
            </button>
          </div>
          {!settings.api_key_configured && (
            <p className="text-sm text-red-500 mt-2">API key not configured - cannot send emails</p>
          )}
        </div>

        {/* Reset to Defaults */}
        {isSuperAdmin && (
          <div className="bg-white rounded-xl border p-6 mb-6">
            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <RefreshCw className="w-5 h-5 text-amber-500" />
              Reset Email Settings
            </h2>
            
            <p className="text-sm text-slate-600 mb-4">
              If emails are failing or using wrong sender addresses, reset to safe production defaults.
              This will remove any API key stored in the database and use the environment variable instead.
            </p>
            
            <button
              onClick={resetToDefaults}
              disabled={resetting}
              className="inline-flex items-center gap-2 px-4 py-2 bg-amber-500 hover:bg-amber-600 text-white rounded-lg transition-colors disabled:opacity-50"
              data-testid="reset-to-defaults-btn"
            >
              {resetting ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
              Reset to Safe Defaults
            </button>
          </div>
        )}

        {/* Debug Section */}
        {isSuperAdmin && (
          <div className="bg-slate-100 rounded-xl border border-slate-300 p-6">
            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-slate-600" />
              Debug: Database & Environment State
            </h2>
            
            <button
              onClick={loadDebugData}
              disabled={debugLoading}
              className="inline-flex items-center gap-2 px-3 py-1.5 bg-slate-600 hover:bg-slate-700 text-white text-sm rounded-lg mb-4"
              data-testid="load-debug-btn"
            >
              {debugLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Eye className="w-3 h-3" />}
              Load Debug Data
            </button>
            
            {debugData && (
              <div className="bg-white rounded-lg border p-4 text-sm">
                {/* Status */}
                <div className={`p-3 rounded-lg mb-4 ${debugData.status === 'HEALTHY' ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
                  <div className="font-medium flex items-center gap-2">
                    {debugData.status === 'HEALTHY' ? (
                      <><CheckCircle className="w-4 h-4 text-green-600" /> Status: HEALTHY</>
                    ) : (
                      <><XCircle className="w-4 h-4 text-red-600" /> Status: NEEDS ATTENTION</>
                    )}
                  </div>
                </div>
                
                {/* Problems */}
                {debugData.problems && debugData.problems.length > 0 && (
                  <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                    <div className="font-medium text-red-800 mb-2">Problems Found:</div>
                    {debugData.problems.map((p, i) => (
                      <div key={i} className="text-red-700 text-sm">
                        • {p.issue} ({p.severity}) - Fix: {p.fix}
                      </div>
                    ))}
                  </div>
                )}
                
                {/* Database */}
                <div className="mb-4">
                  <div className="font-medium text-slate-700 mb-2">Database:</div>
                  <div className="space-y-1 text-slate-600">
                    <div>
                      <span className="text-slate-500">Has resend_api_key:</span>{' '}
                      <span className={debugData.database?.has_resend_api_key ? 'text-red-600 font-medium' : 'text-green-600'}>
                        {debugData.database?.has_resend_api_key ? 'YES (BAD)' : 'NO (GOOD)'}
                      </span>
                    </div>
                    <div><span className="text-slate-500">Sender Email:</span> {debugData.database?.sender_email || 'Not set'}</div>
                    <div><span className="text-slate-500">Sender Name:</span> {debugData.database?.sender_name || 'Not set'}</div>
                    <div><span className="text-slate-500">Reply-To:</span> {debugData.database?.reply_to || 'Not set'}</div>
                  </div>
                </div>
                
                {/* Environment */}
                <div className="mb-4">
                  <div className="font-medium text-slate-700 mb-2">Environment:</div>
                  <div className="space-y-1 text-slate-600">
                    <div>
                      <span className="text-slate-500">RESEND_API_KEY:</span>{' '}
                      <span className={debugData.environment?.RESEND_API_KEY?.present ? 'text-green-600' : 'text-red-600'}>
                        {debugData.environment?.RESEND_API_KEY?.present ? debugData.environment.RESEND_API_KEY.prefix : 'NOT SET'}
                      </span>
                    </div>
                    <div><span className="text-slate-500">EMAIL_FROM:</span> {debugData.environment?.EMAIL_FROM}</div>
                    <div><span className="text-slate-500">SENDER_EMAIL:</span> {debugData.environment?.SENDER_EMAIL}</div>
                  </div>
                </div>
                
                {/* Raw Data */}
                <details className="mt-4">
                  <summary className="cursor-pointer text-slate-500 text-xs">View Raw JSON</summary>
                  <pre className="mt-2 p-2 bg-slate-50 rounded text-xs overflow-auto max-h-64">
                    {JSON.stringify(debugData, null, 2)}
                  </pre>
                </details>
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
}
